/**
 * Utility functions for handling image URLs
 */

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

/**
 * Converts a relative image URL to an absolute URL pointing to the backend
 * @param url - The image URL (can be relative or absolute)
 * @returns Absolute URL pointing to the backend server
 */
export const getImageUrl = (url: string | undefined | null): string => {
  if (!url) return '/placeholder-book.svg';

  // If URL is already absolute (starts with http), return as-is
  if (url.startsWith('http')) {
    return url;
  }

  // If URL is a placeholder or already absolute path, return as-is
  if (url.startsWith('/placeholder') || url.startsWith('data:')) {
    return url;
  }

  // For relative URLs that start with /storage, prepend backend URL
  if (url.startsWith('/storage') || url.startsWith('storage/')) {
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${BASE_URL}${cleanUrl}`;
  }

  // For other relative URLs, assume they need backend prefix
  return `${BASE_URL}/${url}`;
};

/**
 * Gets the primary image URL from a product's images array
 * @param images - Array of product images
 * @param fallbackUrl - Fallback URL if no images found
 * @returns Primary image URL or fallback
 */
export const getPrimaryImageUrl = (
  images: Array<{ fileUrl: string; isPrimary?: boolean }> | undefined,
  fallbackUrl?: string
): string => {
  if (!images || images.length === 0) {
    return getImageUrl(fallbackUrl);
  }
  
  // Find primary image first
  const primaryImage = images.find(img => img.isPrimary);
  if (primaryImage) {
    return getImageUrl(primaryImage.fileUrl);
  }
  
  // Fall back to first image
  return getImageUrl(images[0].fileUrl);
};